import React, { useCallback, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { TwoWayConfig } from '@app/core/models/two-way-config';
import { ListOfReasons } from '../list-of-reasons/ListOfReasons';
import { RelayToggle } from '../relay-button/RelayToggle';
import { RelayActionButton } from '../relay-button/RelayActionButton';
import { ClosedReasonsService } from '../closed-reasons-axios-service/closed-reasons-axios-service';
import { ActionsModal } from '@app/two-way/relay-messenger/components/actions-modal/ActionsModal';
import { Modal } from '@app/two-way/relay-messenger/components/modal/Modal';
import { Plus } from 'baseui/icon';
import { environment } from '@env/environment';
import axios from 'axios';
import { useAlertMessage } from '@app/two-way/relay-messenger/hooks/useAlertMessage/use-alert-message';
import { TwoWayService } from '@app/two-way/relay-messenger/components/relay-messenger-axios-service/relay-messenger-axios-service';

interface SetupConversationsHomeProps {
  clientId: string;
  twoWayConfig: TwoWayConfig;
  isRMAutoResponseEnabled?: boolean;
  isUnassignedEmailNotificationsEnabled?: boolean;
}

export type ClientStatus = {
  type: 'BUSINESS_HOURS' | 'OUT_OF_OFFICE' | 'AFTER_HOURS';
  expires: string;
};

export const SetupConversationsHome = ({
  clientId,
  twoWayConfig,
  isRMAutoResponseEnabled,
  isUnassignedEmailNotificationsEnabled,
}: SetupConversationsHomeProps) => {
  const [css] = useStyletron();
  const { close_reasons_enabled: closeReasonsEnabled } = twoWayConfig;
  const [closedReasons, setClosedReasons] = useState([]);
  const [isClosedConvModalOpen, setIsClosedConvModalOpen] = useState(false);
  const [isReasonTextEmpty, setIsReasonTextEmpty] = useState(false);
  const [isClosedReasonsEnabled, setIsClosedReasonsEnabled] = useState<number>(
    closeReasonsEnabled ? 1 : 0,
  );
  const showMessage = useAlertMessage();

  const [clientStatus, setClientStatus] = useState<ClientStatus | null>(null);
  const [isOutOfOfficeEnabled, setIsOutOfOfficeEnabled] = useState(false);
  const [
    notifyForNewUnassignedConversations,
    setNotifyForNewUnassignedConversations,
  ] = useState<boolean>(false);

  const getClientStatus = useCallback(async () => {
    const status = await TwoWayService.getClientStatus(clientId);
    if (status) {
      const { type, expires } = status as ClientStatus;
      const expiryTime = new Date(expires)
        .toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: 'numeric',
          minute: 'numeric',
          hour: 'numeric',
          timeZoneName: 'short',
        })
        .replace(',', ' at ');
      const formattedStatus = {
        type,
        expires: expiryTime,
      } as ClientStatus;
      setClientStatus(formattedStatus);
    }
  }, [clientId]);

  const getBusinessHoursData = useCallback(async () => {
    const businessHours = await TwoWayService.getBusinessHours(clientId);
    const { outOfOffice } = businessHours;
    setIsOutOfOfficeEnabled(outOfOffice.enabled);
  }, [clientId]);

  const fetchClosedReasons = useCallback(async () => {
    setIsClosedConvModalOpen(false);
    try {
      const closedReasonsRes = await ClosedReasonsService.getClosedReasons(
        clientId,
      );
      const sortedConversations = closedReasonsRes.sort(
        (a, b) => a.ordinal - b.ordinal,
      );
      setClosedReasons(sortedConversations);
    } catch (error) {
      console.error('Failed to fetch close reasons:', error);
    }
  }, [clientId, setIsClosedConvModalOpen, setClosedReasons]);

  const getNotificationPreferences = useCallback(async () => {
    try {
      const { notifyForNewUnassignedConversations } =
        await ClosedReasonsService.getNotificationPreferences(clientId);
      setNotifyForNewUnassignedConversations(
        notifyForNewUnassignedConversations,
      );
    } catch (error) {
      showMessage('Failed to fetch notification preferences', 'error');
      setNotifyForNewUnassignedConversations(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (isUnassignedEmailNotificationsEnabled) {
      getNotificationPreferences();
    }
    if (isRMAutoResponseEnabled) {
      getClientStatus();
      getBusinessHoursData();
      fetchClosedReasons();
    }
  }, []);

  const createNewReason = () => {
    const newReason = {
      clientId,
      reason: '',
      allowAdditionalDetails: false,
      enabled: true,
      archived: false,
    };
    setClosedReasons([...closedReasons, newReason]);
  };

  useEffect(() => {
    // Check if any closed reason has an empty reason text
    const hasEmptyReasonText = closedReasons.some(
      (reason) => reason.reason.trim() === '',
    );
    setIsReasonTextEmpty(hasEmptyReasonText);
  }, [closedReasons]);

  const saveMethod = async (
    closedReasonsArray,
    notifyForNewUnassignedConversations,
  ) => {
    // Save notification preferences
    if (isUnassignedEmailNotificationsEnabled) {
      try {
        await ClosedReasonsService.saveNotificationPreferences(clientId, {
          notifyForNewUnassignedConversations:
            notifyForNewUnassignedConversations,
        });
      } catch (error) {
        showMessage('Failed to save notification preferences', 'error');
        return;
      }
    }

    // Save closed reasons
    try {
      closedReasonsArray = closedReasonsArray.map((reason, ordinal) =>
        Object.assign(reason, { ordinal }),
      );
      const closedReasonsHaveEmptyText = closedReasonsArray.some(
        (reasonObj) => !reasonObj.reason,
      );
      if (
        closedReasonsArray.length ||
        isClosedReasonsEnabled === 1 ||
        isClosedReasonsEnabled === 0
      ) {
        await axios.put(
          `${environment.configURLBase}/client/${clientId}/config/two_way_config`,
          {
            data: {
              ...twoWayConfig,
              close_reasons_enabled: isClosedReasonsEnabled === 1,
            },
          },
        );
      }
      if (closedReasonsHaveEmptyText) {
        setIsReasonTextEmpty(true);
        return;
      }
      const operations = closedReasonsArray.map(async (reasonObj) => {
        const {
          enabled,
          allowAdditionalDetails,
          reason,
          archived,
          id,
          ordinal,
        } = reasonObj;
        if (!id) {
          return ClosedReasonsService.createClosedReasons(
            clientId,
            enabled,
            allowAdditionalDetails,
            reason,
            archived,
            ordinal,
          );
        } else if (id) {
          return ClosedReasonsService.updateClosedReasons(
            clientId,
            id,
            enabled,
            allowAdditionalDetails,
            reason,
            archived,
            ordinal,
          );
        }
      });
      await Promise.all(operations);
      fetchClosedReasons();
      showMessage('Changes have been saved successfully!', 'success');
    } catch (error) {
      showMessage('Failed to save closed reasons', 'error');
      console.error('Error saving closed reasons:', error);
    }
  };

  const disableSaveButton = () => {
    const hasErrorState = closedReasons.some(
      (reasonObj) => reasonObj.isInErrorState,
    );
    if (isReasonTextEmpty || hasErrorState) {
      return true;
    }
    return false;
  };
  return (
    <div
      data-testid="setup-conversations-home"
      className={css({
        paddingBottom: '24px',
      })}
    >
      {/* Out of Office auto response */}
      <div>
        {clientStatus &&
          clientStatus.type === 'OUT_OF_OFFICE' &&
          isOutOfOfficeEnabled &&
          isRMAutoResponseEnabled && (
            <div
              className={css({
                width: '100%',
                padding: '12px 24px',
                margin: '0px',
                backgroundColor: ' #ffc0762e',
                border: '1px solid #fcc076',
              })}
            >
              <p
                className={css({
                  textAlign: 'center',
                  marginBottom: '0px',
                })}
              >
                The Out of Office auto response is enabled until{' '}
                {clientStatus.expires}. Your business hours and other auto
                responses will resume then.
              </p>
            </div>
          )}
      </div>
      <div
        className={css({
          marginLeft: '48px',
          marginRight: '48px',
          marginTop: '24px',
          color: '#5B5A59',
        })}
      >
        {/* Email notifications */}
        {isUnassignedEmailNotificationsEnabled && (
          <div>
            <h4
              className={css({
                fontWeight: '700',
              })}
            >
              Agent Email Notifications
            </h4>
            <p>
              Agents can be notified via email when a new unassigned
              conversation is received.
            </p>
            <div
              className={css({
                display: 'flex',
                justifyContent: 'left',
                alignItems: 'center',
                marginTop: '24px',
              })}
            >
              <p
                className={css({
                  fontWeight: '700',
                  marginRight: '16px',
                  marginBottom: '0px',
                })}
              >
                New conversation email notification
              </p>
              <RelayToggle
                selected={notifyForNewUnassignedConversations ? 1 : 0}
                cb={(_event, index) =>
                  setNotifyForNewUnassignedConversations(index === 1)
                }
              />
            </div>
          </div>
        )}

        {/* Close conversation reasons */}
        <div>
          <h4
            className={css({
              fontWeight: '700',
              marginTop: '48px',
            })}
          >
            Close Conversation Reasons
          </h4>
          <p>
            Relay Messenger can support a high volume of conversations with
            numerous agents responding and resolving issues. To help better
            understand resolutions, messenger admins can configure a list of
            reasons why a conversation was closed.
          </p>
          <div
            className={css({
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
              marginTop: '24px',
            })}
          >
            <p
              className={css({
                fontWeight: '700',
                marginRight: '16px',
                marginBottom: '0px',
              })}
            >
              Require agents to provide reason for closing conversations
            </p>
            <RelayToggle
              selected={isClosedReasonsEnabled}
              cb={(_event, index) => {
                setIsClosedReasonsEnabled(index);
                setIsClosedConvModalOpen(true);
              }}
            />
          </div>
          {isClosedReasonsEnabled === 1 && (
            <Modal
              closeFn={() => setIsClosedConvModalOpen(false)}
              isOpen={isClosedConvModalOpen}
              autoFocus={false}
              content={
                <ActionsModal
                  closeModal={() => {
                    setIsClosedConvModalOpen(false);
                    setIsClosedReasonsEnabled(0);
                  }}
                  successModal={fetchClosedReasons}
                  modalSubTitle={`Are you sure you want to allow the agents to choose a reason for closing a conversation? This will make it mandatory for the agents to choose from a list of pre-defined reasons.\nAs a next step, you will get to edit the list of reasons that the agents will choose from.`}
                  cancelBtnText="Cancel"
                  successBtnText="Yes"
                  dataTestId="confirm-close-reasons-conversation-btn"
                />
              }
              modalTitle="Allow agents to choose a reason?"
              closeable={false}
            />
          )}
          <div
            className={css({
              paddingTop: '24px',
              paddingBottom: '24px',
            })}
          >
            <div
              className={css({
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              })}
            >
              <div
                className={css({
                  fontSize: '14px',
                  color: '#37474F80',
                  fontWeight: '400',
                  lineHeight: '15.6px',
                })}
              >
                NOTE: Reasons will appear to the agents in the same sequence as
                it appears below. Drag and move a reason to modify the sequence.
              </div>
              <RelayActionButton
                disabled={isClosedReasonsEnabled === 0}
                onClick={createNewReason}
                icon={<Plus size={24} />}
                testId="add-reason-btn"
              >
                ADD REASON
              </RelayActionButton>
            </div>
            <ListOfReasons
              closedReasons={closedReasons}
              setClosedReasons={setClosedReasons}
              isClosedReasonsEnabled={isClosedReasonsEnabled}
            />
          </div>
        </div>
      </div>

      {/* Save */}
      <div
        className={css({
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginRight: '48px',
        })}
      >
        <RelayActionButton
          disabled={disableSaveButton()}
          onClick={() => {
            saveMethod(closedReasons, notifyForNewUnassignedConversations);
          }}
          icon={
            <i
              className="fa fa-floppy-o"
              style={{
                fontSize: '14px',
                lineHeight: '1',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            />
          }
          testId="save-close-reasons-btn"
          width="91px"
        >
          Save
        </RelayActionButton>
      </div>
    </div>
  );
};
